<template>
  <div class="enterprise-password-reset">
    <a-steps :current="currentStep - 1" size="small" class="steps">
      <a-step title="手机验证" />
      <a-step title="重置密码" />
      <a-step title="完成" />
    </a-steps>

    <a-spin :spinning="contextLoading">
      <div v-if="currentStep === 1" class="step-panel">
        <div class="context-row">
          <span>目标企业：</span>
          <strong>{{ context.companyName || switchAccount.userName }}</strong>
        </div>
        <div class="context-row">
          <span>验证手机：</span>
          <strong>{{ context.maskedMobile || "加载中..." }}</strong>
        </div>

        <a-form-model ref="verifyForm" :model="form" :rules="verifyRules" layout="vertical">
          <a-row :gutter="15" class="captcha-row">
            <a-col :span="15">
              <a-form-model-item prop="captcha">
                <a-input
                  :key="flowNonce"
                  v-model="form.captcha"
                  :name="captchaInputName"
                  autocomplete="off"
                  placeholder="请输入短信验证码"
                >
                  <a-icon slot="prefix" type="mail" :style="{ color: 'rgba(0,0,0,.25)' }" />
                </a-input>
              </a-form-model-item>
            </a-col>
            <a-col :span="9">
              <a-button
                class="get-captcha"
                block
                :disabled="state.smsSendBtn || state.captchaLoading || contextLoading"
                :loading="state.captchaLoading"
                @click="showCaptcha"
              >
                {{ state.smsSendBtn ? `${state.time}秒后重发` : "获取验证码" }}
              </a-button>
            </a-col>
          </a-row>

          <div id="captcha-enterprise-password-reset" class="captcha-container">
            <AliCaptcha :captcha-config="captchaConfig" />
          </div>

          <a-alert
            v-if="state.showCaptchaMsg"
            class="captcha-tip"
            :message="captchaMessage"
            :type="captchaAlertType"
            show-icon
          />

          <div class="actions single-action">
            <a-button
              type="primary"
              size="large"
              :loading="state.submitLoading"
              :disabled="contextLoading"
              @click="verifyCode"
            >下一步</a-button>
          </div>
        </a-form-model>
      </div>

      <div v-else-if="currentStep === 2" class="step-panel">
        <a-form-model ref="passwordForm" :model="form" :rules="passwordRules" layout="vertical">
          <a-form-model-item prop="password" class="reset-password-item">
            <a-input-password v-model="form.password" placeholder="请输入新密码">
              <a-icon slot="prefix" type="lock" :style="{ color: 'rgba(0,0,0,.25)' }" />
            </a-input-password>
          </a-form-model-item>
          <a-form-model-item prop="confirmPassword" class="reset-password-item">
            <a-input-password v-model="form.confirmPassword" placeholder="请再次输入新密码">
              <a-icon slot="prefix" type="lock" :style="{ color: 'rgba(0,0,0,.25)' }" />
            </a-input-password>
          </a-form-model-item>
          <div class="password-tip">密码长度8-20位，需同时包含大小写字母、数字和特殊字符</div>
          <div class="actions">
            <a-button size="large" :disabled="state.submitLoading" @click="backToVerify">上一步</a-button>
            <a-button type="primary" size="large" :loading="state.submitLoading" @click="submitReset">确认重置</a-button>
          </div>
        </a-form-model>
      </div>

      <div v-else class="step-panel complete-panel">
        <a-result status="success" title="企业切换密码重置成功">
          <template #subTitle>请返回切换账号弹窗，使用新密码切换企业。</template>
          <template #extra>
            <a-button type="primary" @click="$emit('completed')">返回企业切换</a-button>
          </template>
        </a-result>
      </div>
    </a-spin>
  </div>
</template>

<script>
import AliCaptcha from "@/components/AliCaptcha.vue";
import { toBase64Utf8 } from "@/utils/base64";
import {
  enterprisePasswordResetContext,
  sendEnterprisePasswordResetCode,
  verifyEnterprisePasswordResetCode,
  resetEnterprisePassword,
} from "@/api/user/login/Login.js";

export default {
  name: "EnterprisePasswordReset",
  components: { AliCaptcha },
  props: {
    switchAccount: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentStep: 1,
      contextLoading: true,
      context: {},
      resetTicket: "",
      captchaObj: null,
      captchaTimer: null,
      flowNonce: 0,
      captchaInputName: "",
      captchaMessage: "短信验证码将发送至当前主账号绑定手机号。",
      form: {
        captcha: "",
        password: "",
        confirmPassword: "",
      },
      state: {
        time: 60,
        smsSendBtn: false,
        captchaLoading: false,
        submitLoading: false,
        showCaptchaMsg: true,
        captchaStatus: "info",
      },
      captchaConfig: {
        config: {
          captchaId: "e63c338c75217f3dfaccad44160f8369",
          language: "cn",
          product: "bind",
        },
        handler: null,
      },
    };
  },
  computed: {
    verifyRules() {
      return {
        captcha: [{ required: true, message: "请输入短信验证码", trigger: "blur" }],
      };
    },
    passwordRules() {
      return {
        password: [{ validator: this.validatePassword, trigger: "blur" }],
        confirmPassword: [{ validator: this.validateConfirmPassword, trigger: "blur" }],
      };
    },
    captchaAlertType() {
      return { info: "info", success: "success", warning: "warning", error: "error" }[this.state.captchaStatus] || "info";
    },
  },
  created() {
    this.captchaConfig.handler = this.captchaHandler;
    this.resetFlowState();
    this.loadContext();
  },
  beforeDestroy() {
    this.clearCaptchaTimer();
    this.resetTicket = "";
    this.form.captcha = "";
    this.form.password = "";
    this.form.confirmPassword = "";
  },
  methods: {
    resetFlowState() {
      this.clearCaptchaTimer();
      this.flowNonce += 1;
      this.captchaInputName = `enterprise-reset-code-${Date.now()}-${this.flowNonce}`;
      this.currentStep = 1;
      this.context = {};
      this.resetTicket = "";
      this.captchaObj = null;
      this.captchaMessage = "短信验证码将发送至当前主账号绑定手机号。";
      this.form.captcha = "";
      this.form.password = "";
      this.form.confirmPassword = "";
      this.state.time = 60;
      this.state.smsSendBtn = false;
      this.state.captchaLoading = false;
      this.state.submitLoading = false;
      this.state.showCaptchaMsg = true;
      this.state.captchaStatus = "info";
    },
    loadContext() {
      this.contextLoading = true;
      enterprisePasswordResetContext({ switchAccountId: this.switchAccount.accountId })
        .then((res) => {
          if (res && res.code === "200") {
            this.context = res.data || {};
            return;
          }
          this.notifyError("加载失败", (res && res.msg) || "无法获取企业密码重置信息");
          this.$emit("cancel");
        })
        .catch((error) => {
          this.notifyRequestError("加载失败", error);
          this.$emit("cancel");
        })
        .finally(() => {
          this.contextLoading = false;
        });
    },
    showCaptcha() {
      if (!this.captchaObj) {
        this.$message.warning("图形验证码加载中，请稍后重试");
        return;
      }
      this.captchaObj.showCaptcha();
    },
    captchaHandler(captchaObj) {
      this.captchaObj = captchaObj;
      captchaObj
        .appendTo("#captcha-enterprise-password-reset")
        .onSuccess(() => this.sendCode())
        .onError(() => {
          this.notifyError("网络连接异常", "图形验证加载失败，请稍后重试");
        });
    },
    sendCode() {
      const validateResult = this.captchaObj && this.captchaObj.getValidate();
      if (!validateResult) {
        this.$message.warning("请先完成图形验证");
        return;
      }
      this.state.captchaLoading = true;
      sendEnterprisePasswordResetCode({
        switchAccountId: this.switchAccount.accountId,
        lotNumber: validateResult.lot_number,
        captchaOutput: validateResult.captcha_output,
        passToken: validateResult.pass_token,
        genTime: validateResult.gen_time,
      })
        .then((res) => {
          if (res && res.code === "200") {
            this.state.captchaStatus = "success";
            this.captchaMessage = `验证码已发送至 ${this.context.maskedMobile || "绑定手机号"}`;
            this.state.showCaptchaMsg = true;
            this.startCountDown();
            return;
          }
          this.state.captchaStatus = "warning";
          this.captchaMessage = (res && res.msg) || "验证码发送失败，请重试";
        })
        .catch((error) => {
          this.state.captchaStatus = "error";
          this.captchaMessage = this.requestErrorMessage(error, "网络异常，请稍后重试");
        })
        .finally(() => {
          this.state.captchaLoading = false;
        });
    },
    verifyCode() {
      this.$refs.verifyForm.validate((valid) => {
        if (!valid) return;
        this.state.submitLoading = true;
        verifyEnterprisePasswordResetCode({
          switchAccountId: this.switchAccount.accountId,
          captcha: this.form.captcha,
        })
          .then((res) => {
            if (res && res.code === "200" && res.data && res.data.resetTicket) {
              this.resetTicket = res.data.resetTicket;
              this.currentStep = 2;
              return;
            }
            this.notifyError("验证失败", (res && res.msg) || "请重新验证短信验证码");
          })
          .catch((error) => this.notifyRequestError("验证失败", error))
          .finally(() => {
            this.state.submitLoading = false;
          });
      });
    },
    backToVerify() {
      this.resetTicket = "";
      this.form.password = "";
      this.form.confirmPassword = "";
      this.currentStep = 1;
      this.$nextTick(() => {
        if (this.$refs.verifyForm) this.$refs.verifyForm.clearValidate();
      });
    },
    submitReset() {
      if (!this.resetTicket) {
        this.notifyError("验证已失效", "请重新完成手机号验证");
        this.backToVerify();
        return;
      }
      this.$refs.passwordForm.validate((valid) => {
        if (!valid) return;
        this.state.submitLoading = true;
        let password;
        let confirmPassword;
        try {
          password = toBase64Utf8(this.form.password);
          confirmPassword = toBase64Utf8(this.form.confirmPassword);
        } catch (error) {
          this.state.submitLoading = false;
          this.notifyError("编码失败", "密码编码失败，请重新输入");
          return;
        }
        resetEnterprisePassword({
          switchAccountId: this.switchAccount.accountId,
          resetTicket: this.resetTicket,
          password,
          confirmPassword,
        })
          .then((res) => {
            if (res && res.code === "200") {
              this.resetTicket = "";
              this.currentStep = 3;
              this.$notification.success({
                message: "重置成功",
                description: "企业切换密码已重置，请使用新密码切换企业",
              });
              return;
            }
            if (this.isTicketInvalid(res)) {
              this.notifyError("验证已失效", (res && res.msg) || "请重新验证手机号");
              this.backToVerify();
              return;
            }
            this.notifyError("重置失败", (res && res.msg) || "请稍后重试");
          })
          .catch((error) => this.notifyRequestError("重置失败", error))
          .finally(() => {
            this.state.submitLoading = false;
          });
      });
    },
    validatePassword(rule, value, callback) {
      if (!value) return callback("请输入新密码");
      if (value.length < 8 || value.length > 20) return callback("密码长度需为8-20位");
      if (!/[A-Z]/.test(value) || !/[a-z]/.test(value) || !/\d/.test(value) || !/[^a-zA-Z0-9]/.test(value)) {
        return callback("密码需包含大写字母、小写字母、数字和特殊字符");
      }
      callback();
    },
    validateConfirmPassword(rule, value, callback) {
      if (!value) return callback("请再次输入新密码");
      if (value !== this.form.password) return callback("两次输入的密码不一致");
      callback();
    },
    startCountDown() {
      this.clearCaptchaTimer();
      this.state.smsSendBtn = true;
      this.state.time = 60;
      this.captchaTimer = setInterval(() => {
        this.state.time -= 1;
        if (this.state.time <= 0) {
          this.clearCaptchaTimer();
          this.state.smsSendBtn = false;
        }
      }, 1000);
    },
    clearCaptchaTimer() {
      if (this.captchaTimer) {
        clearInterval(this.captchaTimer);
        this.captchaTimer = null;
      }
    },
    isTicketInvalid(res) {
      return /ticket|票据|凭证|过期|失效|无效|重新验证/.test((res && res.msg) || "");
    },
    requestErrorMessage(error, fallback) {
      const data = ((error || {}).response || {}).data || {};
      return data.msg || data.message || fallback;
    },
    notifyRequestError(title, error) {
      this.notifyError(title, this.requestErrorMessage(error, "网络异常，请稍后重试"));
    },
    notifyError(title, description) {
      this.$notification.error({ message: title, description });
    },
  },
};
</script>

<style scoped>
.enterprise-password-reset {
  padding: 8px 20px 12px;
}
.steps {
  margin-bottom: 30px;
}
.step-panel {
  max-width: 420px;
  min-height: 260px;
  margin: 0 auto;
}
.context-row {
  margin-bottom: 12px;
  color: #666;
}
.context-row strong {
  color: #333;
}
.captcha-container {
  height: 0;
  overflow: hidden;
}
.get-captcha {
  display: block;
  width: 100%;
  height: 32px;
  border-radius: 4px;
  font-weight: 500;
}
.captcha-row /deep/ .ant-form-item {
  margin-bottom: 0;
}
.captcha-row /deep/ .ant-input,
.reset-password-item /deep/ .ant-input {
  height: 32px !important;
  border-radius: 4px;
}
.reset-password-item {
  margin-bottom: 12px;
}
.captcha-tip {
  margin: 10px 0 18px;
  font-size: 13px;
}
.captcha-tip /deep/ .ant-alert-message {
  color: #666;
  font-size: 13px;
}
.password-tip {
  margin-top: -8px;
  color: #999;
  font-size: 12px;
}
.actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 28px;
}
.actions .ant-btn {
  min-width: 120px;
}
.single-action .ant-btn {
  min-width: 180px;
}
.complete-panel {
  text-align: center;
}
</style>
